/**
 * Pure decisions for the WhatsApp OTP sign-in (OTPLogin.tsx), kept out of the component so
 * they can be tested without React.
 *
 * Server contract (server/routes.ts, POST /api/auth/verify-otp and /verify-otp/choose):
 *  - one eligible account      → { success:true, token, user }
 *  - several eligible accounts → { success:true, needsAccountChoice:true, choiceToken,
 *                                  accounts:[{ id, role, maskedEmail }] } — NO token; the JWT is
 *                                  minted only by POST /verify-otp/choose { choiceToken, userId }.
 *                                  The choiceToken is SINGLE-USE: any failure there means restart.
 *  - MFA account               → { mfaRequired:true } (403 → apiRequest throws)
 *  - Redis down while choosing → 503 "Please sign in with email and password"
 *
 * The bug this fixes: the chooser response was treated as a sign-in, so setToken(undefined)
 * ran and the user sat on /login. In the mobile app OTP is the ONLY sign-in, so those couples
 * were locked out entirely.
 */
export interface ChooserAccount { id: number; role: string | null; maskedEmail: string }

export type VerifyOutcome =
  | { kind: "signed_in"; token: string; user: any }
  | { kind: "choose"; choiceToken: string; accounts: ChooserAccount[] }
  | { kind: "mfa"; message?: string }
  | { kind: "failed"; message: string };

export function classifyVerifyResponse(data: any): VerifyOutcome {
  if (!data || typeof data !== "object") return { kind: "failed", message: "Invalid verification code" };
  if (data.mfaRequired) return { kind: "mfa", message: typeof data.message === "string" ? data.message : undefined };
  if (data.success && data.needsAccountChoice) {
    const accounts: ChooserAccount[] = Array.isArray(data.accounts)
      ? data.accounts
          .filter((a: any) => a && Number.isFinite(Number(a.id)))
          .map((a: any) => ({ id: Number(a.id), role: a.role ?? null, maskedEmail: String(a.maskedEmail ?? "") }))
      : [];
    if (typeof data.choiceToken === "string" && data.choiceToken && accounts.length > 0) {
      return { kind: "choose", choiceToken: data.choiceToken, accounts };
    }
    return { kind: "failed", message: "We couldn't finish signing you in. Please try again." };
  }
  // A success WITHOUT a token is never a sign-in (never persist `undefined`).
  if (data.success && typeof data.token === "string" && data.token) {
    return { kind: "signed_in", token: data.token, user: data.user };
  }
  return { kind: "failed", message: typeof data.message === "string" && data.message ? data.message : "Invalid verification code" };
}

/**
 * The server's "use email and password instead" advice (MFA accounts, Redis-down chooser).
 * The app hides email sign-in, so it must never be told to use it.
 */
export function emailFallbackMessage(app: boolean, serverMessage?: string): string {
  if (app) {
    return "This account can't sign in with WhatsApp in the app yet. Please sign in at lamirage.co, or contact us on WhatsApp: +91 94467 76666.";
  }
  return serverMessage || "This account requires email login with password. Please use the email login option.";
}

/** Server text that points at email sign-in — rewritten in the app. */
export function isEmailFallbackText(message: string | undefined | null): boolean {
  return !!message && /sign in with email|email login|email and password/i.test(message);
}

const ROLE_LABELS: Record<string, string> = {
  client: "Couple account",
  vendor: "Vendor account",
};
export function chooserRoleLabel(role: string | null): string {
  if (!role) return "Account";
  return ROLE_LABELS[role] ?? role.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) + " account";
}
